import { AxiosError, AxiosResponse } from "axios";
import { dialogMixins } from "./dialog";
import { formattingMixins } from "./formatting";
import { throbberMixins } from "./throbber";
import axios from "@/main";
import { saveAs } from "file-saver";
import { GatewayResponse } from "@/models/gateway";

const defaultErrorMessage = "Сервис временно недоступен, повторите попытку позже";

export interface AxiosMixin {
  getBaseUrl(): string;
  getBaseScheme(): string;
  handleBadRequest(value: AxiosError, badRequestMessage?: string, conflictMessage?: string): void;

  createRequestAsync<T>(
    method: HttpMethod,
    url: string,
    data?: any,
    headers?: any,
    succesMessageType?: MessageType,
    errorMessageType?: MessageType
  ): Promise<T | undefined>;
  downloadAsync(url: string): Promise<boolean>;

  toBodyFormData(obj: object): FormData;
}

export const axiosMixin: AxiosMixin = {
  getBaseUrl(): string {
    if (process.env.VUE_APP_BASE_SCHEME && process.env.VUE_APP_BASE_URL) {
      return `${process.env.VUE_APP_BASE_SCHEME}://${process.env.VUE_APP_BASE_URL}`;
    }
    return window.location.origin;
  },
  getBaseScheme(): string {
    if (process.env.VUE_APP_BASE_SCHEME) {
      return process.env.VUE_APP_BASE_SCHEME;
    }
    return window.location.protocol.replace(/:/, "");
  },
  handleBadRequest(error: AxiosError, badRequestMessage?: string, conflictMessage?: string): void {
    if (error.response?.status == 400) {
      dialogMixins.showError(badRequestMessage || defaultErrorMessage, true);
    } else if (error.response?.status == 409) {
      dialogMixins.showError(conflictMessage || defaultErrorMessage, true);
    } else {
      dialogMixins.showError(defaultErrorMessage);
    }
  },
  createRequestAsync<T>(
    method: HttpMethod,
    url: string,
    data?: any,
    headers = undefined,
    successNotificationType = MessageType.notification,
    errorNotificationType = MessageType.dialog
  ): Promise<T | undefined> {
    return new Promise<T | undefined>((resolve, reject) => {
      throbberMixins.showThrobber();
      axios<Response<T>>({ method: method, url: url, data: data, headers: headers })
        .then((response) => {
          if (response.data?.message) {
            if (successNotificationType == MessageType.dialog) {
              dialogMixins.showNotificationDialog("Уведомление", response.data.message, "success", "OK");
            } else if (successNotificationType == MessageType.notification) {
              dialogMixins.showMessage(response.data.message, "success", false);
            }
          }
          const data = response.data?.data;
          resolve(data);
        })
        .catch((error: AxiosError) => {
          if ((error.config as any)?.silentResponseMessage) {
            reject();
            return;
          }
          const response = error.response?.data as Response<T>;
          const isGatewayError = !(
            (response && response.errorMessage && !formattingMixins.isEmptyString(response.errorMessage)) ||
            response.displayMessage
          );
          const errorMessage = isGatewayError
            ? defaultErrorMessage
            : response.errorMessage ?? defaultErrorMessage;
          if (isGatewayError) {
            dialogMixins.showError(errorMessage, true);
          } else {
            if (errorNotificationType == MessageType.dialog) {
              dialogMixins.showNotificationDialog("Ошибка", errorMessage, "warning", "OK");
            } else if (errorNotificationType == MessageType.notification) {
              dialogMixins.showError(errorMessage, true);
            }
          }
          reject();
        })
        .finally(() => throbberMixins.hideThrobber());
    });
  },

  async downloadAsync(url: string): Promise<boolean> {
    try {
      throbberMixins.showThrobber();
      const response = await axios.post(url, undefined, {
        responseType: "arraybuffer",
      });

      const contentDispositionHeader = response.headers["content-disposition"];
      if (contentDispositionHeader) {
        const fileNameMatches = contentDispositionHeader.match(/filename="?([^"]+)"?;/);
        const fileName = fileNameMatches && fileNameMatches.length > 1 ? fileNameMatches[1] : "";
        const file = new Blob([response.data]);
        saveAs(file, fileName);
      }
    } catch (error: any) {
      dialogMixins.showError(defaultErrorMessage, true);
      return false;
    } finally {
      throbberMixins.hideThrobber();
    }
    return true;
  },

  toBodyFormData(obj: object): FormData {
    const bodyFormData = new FormData();
    Object.entries(obj).forEach(([key, item]) => {
      if (item != undefined) {
        bodyFormData.append(key, item);
      }
    });
    return bodyFormData;
  },
};

export default {
  methods: axiosMixin,
};

export class Response<T> {
  data?: T;
  errorMessage?: string;
  displayMessage?: string;
  message?: string;
}

export enum HttpMethod {
  get = "get",
  post = "post",
  put = "put",
  delete = "delete",
}

export enum MessageType {
  none = "none",
  dialog = "dialog",
  notification = "notification",
}
